import React from "react";
import { FaPaperclip, FaRegComment } from "react-icons/fa";
import PriorityIcon from "./atoms/PriorityIcon";
import LabelChip from "./atoms/LabelChip";
import { AssigneeAvatars } from "./AssigneeChips";
import { ParentKey, TaskCounts } from "./atoms/TaskCounts";
import OverdueBadge from "./atoms/OverdueBadge";
import { isOverdue, toDateOnly, projectFrame } from "./taskConstants";

const MAX_LABELS = 3;

// Bitiş bir "gün" alanı: YYYY-MM-DD kısmı yerel gün olarak okunur (saat dilimi kayması olmasın)
const formatDue = (d) => {
  const date = toDateOnly(d);
  return date ? date.toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "";
};

// Saf kart içeriği; sürükleme sarmalayıcısı TaskBoard'da (SortableCard).
// children: aynı sütundaki alt işler (Client#127) — kartın gövdesinde, "Alt işler" kutusunda.
// Görünüm: proje renginde çerçeve + tam genişlik renkli başlık şeridi, gövde beyaz
// (gecikmiş ve bitmemişse gövde kırmızı yanıp söner).
const TaskCard = React.forwardRef(function TaskCard(
  { task, onClick, dragDisabled = false, className = "", onKeyDown, children, ...rest },
  ref
) {
  const labels = task.labels || [];
  const extra = labels.length - MAX_LABELS;
  const overdue = isOverdue(task);
  const frame = projectFrame(task.project?.color);
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-disabled={dragDisabled ? "true" : undefined}
      onClick={() => onClick && onClick(task)}
      onKeyDown={(e) => {
        // Enter kartı açar; diğer tuşlar (Space → klavye ile sürükleme) dnd-kit'e gider.
        if (e.key === "Enter") {
          if (onClick) onClick(task);
          return;
        }
        if (onKeyDown) onKeyDown(e);
      }}
      className={`text-left border-2 ${frame.frame} rounded-xl overflow-hidden flex flex-col hover:shadow-md transition-shadow ${
        dragDisabled ? "cursor-default" : "cursor-grab active:cursor-grabbing"
      } ${className}`}
      data-testid="task-card"
      {...rest}
    >
      <div className={`flex items-center justify-between gap-2 px-3 py-1.5 ${frame.header}`} data-testid="task-card-header">
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-SemiBold shrink-0">{task.key}</span>
          <ParentKey parent={task.parent} className="truncate" />
        </span>
        <PriorityIcon priority={task.priority} />
      </div>
      <div className={`p-3 flex flex-col gap-2 ${overdue ? "animate-staleBlink" : "bg-white"}`} data-testid="task-card-body">
        <div
          className="text-sm font-Medium text-gray-800 break-words"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
        >
          {task.title}
        </div>
        {labels.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {labels.slice(0, MAX_LABELS).map((l) => (
              <LabelChip key={l._id} label={l} />
            ))}
            {extra > 0 && <span className="text-xs text-gray-500 font-Medium self-center">+{extra}</span>}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
          <div className="flex items-center gap-3 min-w-0">
            {overdue && <OverdueBadge />}
            {task.dueDate && (
              <span className={overdue ? "text-red-700 font-Medium" : ""}>{formatDue(task.dueDate)}</span>
            )}
            {task.attachmentCount > 0 && (
              <span className="inline-flex items-center gap-1" title="Ek">
                <FaPaperclip className="w-3 h-3" aria-hidden="true" />
                {task.attachmentCount}
              </span>
            )}
            <TaskCounts task={task} />
            {task.commentCount > 0 && (
              <span className="inline-flex items-center gap-1" title="Yorum">
                <FaRegComment className="w-3 h-3" aria-hidden="true" />
                {task.commentCount}
              </span>
            )}
          </div>
          <AssigneeAvatars users={task.assignees} />
        </div>
        {React.Children.toArray(children).length > 0 && (
          // İç kartlara tıklama/klavye üst kartı açmasın.
          <div
            className="flex flex-col gap-1.5 border-t border-gray-100 pt-2 cursor-default"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
            data-testid="card-subtasks"
          >
            <span className="text-xs text-gray-500 font-Medium">Alt işler</span>
            {children}
          </div>
        )}
      </div>
    </div>
  );
});

// Üst kartın gövdesindeki küçük alt iş kartı. showParent: tek başına sürüklenirken "↳ KEY".
export const SubtaskCard = React.forwardRef(function SubtaskCard(
  { task, onClick, dragDisabled = false, className = "", showParent = false, onKeyDown, ...rest },
  ref
) {
  const overdue = isOverdue(task);
  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-disabled={dragDisabled ? "true" : undefined}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) onClick(task);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") {
          if (onClick) onClick(task);
          return;
        }
        if (onKeyDown) onKeyDown(e);
      }}
      className={`flex items-center gap-2 border rounded-lg px-2 py-1.5 ${
        overdue ? "animate-staleBlink border-red-300" : "bg-white border-gray-200 hover:border-gray-300"
      } ${
        dragDisabled ? "cursor-default" : "cursor-grab active:cursor-grabbing"
      } ${className}`}
      data-testid="subtask-card"
      {...rest}
    >
      <span className="text-xs text-gray-500 font-Medium shrink-0">{task.key}</span>
      {showParent && <ParentKey parent={task.parent} className="shrink-0" />}
      <span className="text-xs text-gray-800 truncate flex-1 min-w-0" title={task.title}>{task.title}</span>
      {overdue && <OverdueBadge compact />}
      <PriorityIcon priority={task.priority} />
      <AssigneeAvatars users={task.assignees} max={2} />
    </div>
  );
});

// Üst işi başka sütunda olan alt işlerin çerçevesi (gölge üst kart): üst işli görünüm
// her sütunda korunur. Sürüklenmez; tıklanınca üst işi açar. statusLabel: üst işin
// asıl bulunduğu sütun.
export function GhostParentCard({ parent, project, statusLabel, onClick, children }) {
  const open = () => onClick && onClick(parent);
  const frame = projectFrame(project?.color);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter") open();
      }}
      className={`text-left border-2 border-dashed ${frame.frame} rounded-xl overflow-hidden flex flex-col cursor-pointer hover:shadow-md transition-shadow`}
      data-testid="ghost-parent-card"
    >
      <div className={`flex items-center justify-between gap-2 px-3 py-1.5 ${frame.header}`}>
        <span className="text-xs font-SemiBold shrink-0">{parent?.key}</span>
        {statusLabel && (
          <span className="text-[10px] bg-white/70 rounded px-1.5 py-0.5 truncate" title="Üst işin bulunduğu sütun">
            Üst iş · {statusLabel}
          </span>
        )}
      </div>
      <div className="p-3 flex flex-col gap-2 bg-white">
        <div
          className="text-sm font-Medium text-gray-500 break-words"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
        >
          {parent?.title}
        </div>
        <div
          className="flex flex-col gap-1.5 border-t border-gray-100 pt-2 cursor-default"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          role="presentation"
          data-testid="card-subtasks"
        >
          <span className="text-xs text-gray-500 font-Medium">Alt işler</span>
          {children}
        </div>
      </div>
    </div>
  );
}

export default TaskCard;
